import React, { useEffect } from 'react';
import {
    Container, Typography, Box, Paper, Divider, Select, MenuItem, FormControl,
    InputLabel, TextField, Button, Stack, Grid, Switch, FormControlLabel, TextareaAutosize
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppStore } from '../../../../../store/appStore.tsx';
import { useValue } from '../../../../../context/ContextProvider';
import { useTheme } from '@emotion/react';
import { getWirelessCode, updateConfigAlarma } from '../../../../../actions/alarmaActions.js';

// --- Opciones para los campos Select ---
const ZONAS_OPTIONS = Array.from({ length: 8 }, (_, i) => ({ value: i, label: `Zona ${i + 1}` }));
const HABILITACION_OPTIONS = [
    { value: 0x00, label: 'No Habilitada (0x00)' }, { value: 0x10, label: 'Habilitada (0x10)' },
    { value: 0x20, label: 'Botón de Pánico (0x20)' }, { value: 0x22, label: 'Sensor de Humo (0x22)' }, { value: 0x24, label: 'Sensor de Gas (0x24)' },
];
const ESTADO_NORMAL_OPTIONS = [
    { value: 0x01, label: 'Normalmente Cerrado (0x01)' }, { value: 0x02, label: 'Normalmente Abierto (0x02)' }, { value: 0x03, label: 'Impedancia Fija EOL (0x03)' },
];
const SENSIBILIDAD_OPTIONS = [
    { value: 0, label: 'Máxima' }, { value: 1, label: 'Alta' }, { value: 2, label: 'Media Alta' }, { value: 3, label: 'Normal' }, { value: 4, label: 'Media Baja' }, { value: 5, label: 'Baja' }, { value: 6, label: 'Baja Baja' }, { value: 7, label: 'Mínima' },
];

// --- Funciones de ayuda para conversión de tiempo ---
const totalSecondsToHms = (totalSeconds) => {
    if (isNaN(totalSeconds) || totalSeconds < 0) return { h: 0, m: 0, s: 0 };
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return { h, m, s };
};

const hmsToTotalSeconds = (timeObj) => {
    if (!timeObj) return 0;
    return (timeObj.h || 0) * 3600 + (timeObj.m || 0) * 60 + (timeObj.s || 0);
};


// --- Componente Reutilizable para Input de Tiempo (HH:MM:SS) ---
const TimeInputBox = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    position: 'relative',
}));

const TimeInputLabel = styled(Typography)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: theme.spacing(2),
    transform: 'translateY(-50%)',
    background: theme.palette.background.paper,
    padding: theme.spacing(0, 0.5),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
}));

// El componente ahora recibe y emite el tiempo como un número total de segundos
const TimeInput = ({ label, name, value = 0, onChange }) => {
    const timeObj = totalSecondsToHms(value); // Convierte segundos a {h, m, s} para mostrar

    const handleComponentChange = (component) => (event) => {
        let val = parseInt(event.target.value, 10);
        if (isNaN(val)) val = 0;

        const max = component === 'h' ? 23 : 59;
        val = Math.max(0, Math.min(val, max));

        const updatedTimeObj = { ...timeObj, [component]: val };
        const totalSeconds = hmsToTotalSeconds(updatedTimeObj); // Convierte de vuelta a segundos

        // Emite un evento sintético con el valor numérico (segundos)
        const syntheticEvent = {
            target: { name, value: totalSeconds, type: 'number' }
        };
        onChange(syntheticEvent);
    };

    return (
        <TimeInputBox>
            <TimeInputLabel>{label}</TimeInputLabel>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <TextField label="HH" type="number" value={String(timeObj.h).padStart(2, '0')} onChange={handleComponentChange('h')} inputProps={{ style: { textAlign: 'center' } }} size="small" sx={{ width: 75 }} />
                <Typography variant="h6">:</Typography>
                <TextField label="MM" type="number" value={String(timeObj.m).padStart(2, '0')} onChange={handleComponentChange('m')} inputProps={{ style: { textAlign: 'center' } }} size="small" sx={{ width: 75 }} />
                <Typography variant="h6">:</Typography>
                <TextField label="SS" type="number" value={String(timeObj.s).padStart(2, '0')} onChange={handleComponentChange('s')} inputProps={{ style: { textAlign: 'center' } }} size="small" sx={{ width: 75 }} />
            </Stack>
        </TimeInputBox>
    );
};

// --- Generador del estado inicial para las 8 zonas ---
// Ahora los delays se guardan como segundos.
export const generarEstadoInicialZonas = () => {
    return Array.from({ length: 8 }, (_, i) => ({
        nZona: i,
        hab: 0x00,
        estNorm: 0x03,
        sens: 2,
        dlySal: 30, // en segundos
        dlyIng: 30, // en segundos
        byp: 0,
        wlessCod: 0,
        desCorta: "---",
        desLarga: "---",
    }));
};


function ConfiguracionZonasForm({ config, setConfig, state, setState, originalConfigRef }) {
    const { zonaSeleccionada, jsonOutput, formDirty, jsonNeedsUpdate } = state;
    const theme = useTheme();
    const { currentUser, device, startLoading, endLoading } = useAppStore();
    const { dispatch } = useValue();
    

    useEffect(() => {
        const originalConfigString = JSON.stringify(originalConfigRef.current);
        const currentConfigString = JSON.stringify(config);
        const isDirty = currentConfigString !== originalConfigString;

        let needsUpdate = true;
        try {
            const jsonObject = JSON.parse(jsonOutput || '[]');
            // Ya no se necesita preparePayload, se compara directamente con el estado `config`
            needsUpdate = JSON.stringify(jsonObject) !== currentConfigString;
        } catch (e) {
            needsUpdate = true;
        }

        if (isDirty !== formDirty || needsUpdate !== jsonNeedsUpdate) {
            setState(prev => ({
                ...prev,
                formDirty: isDirty,
                jsonNeedsUpdate: needsUpdate
            }));
        }
    }, [config, jsonOutput, formDirty, jsonNeedsUpdate, originalConfigRef, setState]);

    // La función preparePayload ya no es necesaria.

    const handleZonaChange = (event) => {
        setState(prev => ({ ...prev, zonaSeleccionada: event.target.value }));
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        // La lógica para 'time_object' se elimina, ya que TimeInput ahora emite un 'number'
        let valorFinal = type === 'checkbox' ? (checked ? 1 : 0) : value;

        const nuevasConfiguraciones = config.map((zona, index) => {
            if (index === zonaSeleccionada) {
                return { ...zona, [name]: valorFinal };
            }
            return zona;
        });
        setConfig(nuevasConfiguraciones);
    };

    const handleGenerateJson = () => {
        setState(prev => ({
            ...prev,
            // El JSON se genera directamente desde `config`
            jsonOutput: JSON.stringify(config, null, 2),
            jsonNeedsUpdate: false
        }));
    };

    const sendConfig = async () => {
        // El payload es directamente el estado `config`
        //const payload = config;
        //console.log("--- Enviando Configuración de Zonas JSON ---", config);
        startLoading();
        setState(prev => ({ ...prev, configId: "---", estado: "Esperando respuesta del servidor/dispositivo", color: "black" }));
        const updateResult = await updateConfigAlarma(currentUser, device.devId, "CMD_SET_CONFIG_ZONAS", config, dispatch);
        //console.log("updateResult: ", updateResult);

        if (updateResult?.deviceResponse === 0) {
            originalConfigRef.current = JSON.parse(JSON.stringify(config));
            setState(prev => ({ ...prev, formDirty: false, configId: updateResult.insertId, estado: "Última configuración actualizada con éxito.", color: "green" }));
            alert(`Configuración enviada con éxito (ID: ${updateResult.insertId})`);
        } else {
            setState(prev => ({ ...prev, configId: 0, estado: "Error. Última configuración NO actualizada.", color: "red" }));
            alert("Error al enviar la configuración.");
        }
        endLoading();
    };

    const handleCancel = () => {
        if (window.confirm("¿Seguro que quieres descartar los cambios?")) {
            const originalConfig = originalConfigRef.current;
            setConfig(originalConfig);
            setState(prev => ({
                ...prev,
                // El JSON también se revierte a la versión original
                jsonOutput: JSON.stringify(originalConfig, null, 2),
                jsonNeedsUpdate: false,
            }));
        }
    };

    const handleGetWirelessCode = async() => {
        const resp = await getWirelessCode(currentUser, device.devId, 'GET_WIRELESS_CODE', dispatch);  //1100 es el comando para configurar zonas
        //console.log(wirelessCode.respFromMcu.respuesta);
        if(resp && resp.respFromMcu.respuesta === 0)
        {
            const wirelessCode = (resp.respFromMcu.codigo >>> 4);   // '>>>' es shift sin signo, porque '>>' es con signo
            //console.log(config);
            const nuevasZonas = config.map(zona => {
                // Si es la zona que queremos modificar...
                if (zona.nZona === zonaSeleccionada) {
                    // Retornamos un NUEVO objeto copiando todo lo de la zona (...zona) 
                    // pero sobreescribiendo 'estNorm'
                    return { ...zona, wlessCod:  wirelessCode}; 
                }
                // Si no es la zona 2, retornamos el objeto intacto
                return zona; 
            });
            
            // Finalmente, actualizas tu estado de React
            setConfig(nuevasZonas);
        }    
    };

    const datosZonaActual = config?.[zonaSeleccionada] || {};
    const getLabelFromValue = (options, value) => options.find(o => o.value === value)?.label || 'N/A';

    // Función para formatear segundos a HH:MM:SS para el resumen
    const formatSecondsToHms = (totalSeconds) => {
        const timeObj = totalSecondsToHms(totalSeconds);
        return `${String(timeObj.h).padStart(2, '0')}:${String(timeObj.m).padStart(2, '0')}:${String(timeObj.s).padStart(2, '0')}`;
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>Configuración de Zonas</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box component="form" onSubmit={(e) => { e.preventDefault(); sendConfig(); }}>
                    <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
                        {/* Columna izquierda: Formulario */}
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>
                                    ZONAS
                                </Typography>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Seleccionar Zona</InputLabel>
                                    <Select value={zonaSeleccionada} label="Seleccionar Zona" onChange={handleZonaChange}>
                                        {ZONAS_OPTIONS.map(({ value, label }) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Divider><Typography variant="subtitle1">Datos para {ZONAS_OPTIONS.find(z => z.value === zonaSeleccionada)?.label}</Typography></Divider>

                                <Grid container spacing={2} sx={{ pt: 2 }}>
                                    {/* ... Otros campos del formulario ... */}
                                    <Grid item xs={12}> <TextField select fullWidth name="hab" label="Habilitación" value={datosZonaActual.hab ?? 0x00} onChange={handleChange}> {HABILITACION_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)} </TextField> </Grid>
                                    <Grid item xs={12}> <TextField select fullWidth name="estNorm" label="Estado Normal" value={datosZonaActual.estNorm ?? 0x03} onChange={handleChange}> {ESTADO_NORMAL_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)} </TextField> </Grid>
                                    <Grid item xs={12}> <TextField select fullWidth name="sens" label="Sensibilidad" value={datosZonaActual.sens ?? 2} onChange={handleChange}> {SENSIBILIDAD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)} </TextField> </Grid>

                                    {/* El componente TimeInput ahora usa directamente el valor en segundos */}
                                    <Grid item xs={12}>
                                        <TimeInput label="Delay Salida" name="dlySal" value={datosZonaActual.dlySal} onChange={handleChange} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TimeInput label="Delay Entrada" name="dlyIng" value={datosZonaActual.dlyIng} onChange={handleChange} />
                                    </Grid>

                                    {/* ... Resto de los campos del formulario ... */}
                                    <Grid item xs={12}> <TextField fullWidth name="desCorta" label="Descripción Corta" value={datosZonaActual.desCorta ?? ""} onChange={handleChange} inputProps={{ maxLength: 8 }} helperText="Máximo 8 caracteres" /> </Grid>
                                    <Grid item xs={12}> <TextField fullWidth name="desLarga" label="Descripción Larga" value={datosZonaActual.desLarga ?? ""} onChange={handleChange} inputProps={{ maxLength: 30 }} helperText="Máximo 30 caracteres" /> </Grid>

                                    <Grid item xs={12}>
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <Box sx={{ flexGrow: 1 }}>
                                                <TextField fullWidth type="number" name="wlessCod" label="Código Inalámbrico" value={datosZonaActual.wlessCod ?? ""} onChange={handleChange} inputProps={{ maxLength: 14 }} helperText="Máximo 14 caracteres" />
                                            </Box>
                                            <Box sx={{
                                                display: 'flex', 
                                                alignItems: 'flex-start', // Alinea el botón hacia arriba
                                                pt: 1 // (padding-top) empuja ligeramente el botón hacia abajo para centrarlo con la caja de texto
                                            }}>
                                                <Button variant="outlined" onClick={handleGetWirelessCode}> Recuperar </Button>
                                            </Box>
                                        </Stack>
                                    </Grid>



                                    <Grid item xs={12}> <FormControlLabel control={<Switch name="byp" checked={!!datosZonaActual.byp} onChange={handleChange} />} label="Zona Bypass" /> </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Columna derecha: JSON y Resumen */}
                        <Grid
                            item
                            xs={12}
                            md={6}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }}
                        >
                            {/* Panel de JSON */}
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    pt: 4
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 12,
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'background.paper',
                                        px: 1,
                                        color: 'primary.main',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    JSON de Configuración
                                </Typography>

                                {jsonOutput ? (
                                    <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                                        <TextareaAutosize
                                            value={jsonOutput}
                                            minRows={10}
                                            maxRows={36}
                                            style={{
                                                width: '100%',
                                                border: `1px solid ${jsonNeedsUpdate ? '#ff9800' : '#ccc'}`,
                                                borderRadius: '4px',
                                                padding: '8px',
                                                fontFamily: 'monospace',
                                                resize: 'vertical',
                                                backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : 'white'
                                            }}
                                            readOnly
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            flexGrow: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Typography color="text.secondary">
                                            Genera el JSON para previsualizarlo aquí.
                                        </Typography>
                                    </Box>
                                )}

                                <Button
                                    variant="contained"
                                    fullWidth
                                    color={jsonNeedsUpdate ? "warning" : "primary"}
                                    onClick={handleGenerateJson}
                                    sx={{ mt: 'auto' }}
                                >
                                    {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                                </Button>
                            </Paper>


                            {/* Panel de Resumen */}
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>
                                    RESUMEN DE CONFIGURACIÓN
                                </Typography>
                                <Typography variant="h6" gutterBottom>{ZONAS_OPTIONS.find(z => z.value === zonaSeleccionada)?.label}</Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2"><strong>Habilitación:</strong> {getLabelFromValue(HABILITACION_OPTIONS, datosZonaActual.hab)}</Typography>
                                        <Typography variant="body2"><strong>Estado Normal:</strong> {getLabelFromValue(ESTADO_NORMAL_OPTIONS, datosZonaActual.estNorm)}</Typography>
                                        <Typography variant="body2"><strong>Sensibilidad:</strong> {getLabelFromValue(SENSIBILIDAD_OPTIONS, datosZonaActual.sens)}</Typography>
                                        <Typography variant="body2"><strong>Bypass:</strong> {datosZonaActual.byp ? 'Sí' : 'No'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        {/* Se usa la nueva función para formatear el tiempo del resumen */}
                                        <Typography variant="body2"><strong>Delay Salida:</strong> {formatSecondsToHms(datosZonaActual.dlySal)}</Typography>
                                        <Typography variant="body2"><strong>Delay Entrada:</strong> {formatSecondsToHms(datosZonaActual.dlyIng)}</Typography>
                                        <Typography variant="body2"><strong>Desc. Corta:</strong> {datosZonaActual.desCorta || '---'}</Typography>
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" color={formDirty ? 'error' : 'inherit'}><strong>Formulario Modificado:</strong> {formDirty ? 'Sí' : 'No'}</Typography>
                                            <Typography variant="body2" color={jsonNeedsUpdate ? 'warning.main' : 'inherit'}><strong>JSON Actualizado:</strong> {jsonNeedsUpdate ? 'No' : 'Sí'}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* ... Resto del JSX sin cambios (Resultado y Botones) ... */}
                        <Grid item xs={12}> <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}> <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase' }}> Resultado envío al Dispositivo </Typography> <Box> <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5 }}> <strong>ConfigId: </strong>{state.configId} </Typography> <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5, color: state.color }}> <strong>Estado: </strong>{state.estado} </Typography> </Box> </Paper> </Grid>
                        <Grid item xs={12}> <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}> <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={!formDirty}>Cancelar</Button> <Button type="submit" variant="contained" disabled={jsonNeedsUpdate} > Enviar </Button> </Stack> </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Container>
    );
}

export default ConfiguracionZonasForm;